import { useCallback, useRef, useState } from 'react';
import {
	type OperationVariables,
	type MutationResult,
	type MutationFunctionOptions,
} from '@apollo/react-common';
import {
	type MutationHookOptions,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- `@confluence/apollo-relay-interop` package is the only one where such imports are allowed
	useMutation,
} from '@apollo/react-hooks';
import { type GraphQLTaggedNode, commitMutation, useRelayEnvironment } from 'react-relay';

import { useMemoObj } from './useMemoObj';
import { toDocumentNode } from './toDocumentNode';

export type MutationFunctionInteropSupportedOptions<TData, TVariables> = Pick<
	MutationFunctionOptions<TData, TVariables>,
	'variables' | 'optimisticResponse' | 'update'
>;

export type MutationFunctionInterop<TData, TVariables> = (
	options?: MutationFunctionInteropSupportedOptions<TData, TVariables>,
) => Promise<{ data?: TData }>;

export type UseMutationInteropSupportedResult<TData, TVariables> = [
	MutationFunctionInterop<TData, TVariables>,
	Pick<MutationResult<TData>, 'data' | 'error' | 'loading'>,
];

export type UseMutationInteropSupportedArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> =
	| [GraphQLTaggedNode]
	| [
			GraphQLTaggedNode,
			Pick<
				MutationHookOptions<TData, TVariables>,
				| 'variables'
				| 'onCompleted'
				| 'onError'
				| 'errorPolicy'
				| 'optimisticResponse'
				// TODO: `update` should disallow first arg (in favor of interop cache reads/writes)
				| 'update'
				// TODO: update `refetchQueries` to only accept GraphQLTaggedNode
				| 'refetchQueries'
				| 'context'
				| 'fetchPolicy'
				| 'awaitRefetchQueries'
			>,
	  ];

export function useMutationInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	...args: UseMutationInteropSupportedArgs<TData, TVariables>
): UseMutationInteropSupportedResult<TData, TVariables> {
	if (process.env.BUILD_VARIANT === 'relay') {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useMutationInteropRelay<TData, TVariables>(...args);
	} else {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useMutation<TData, TVariables>(...[toDocumentNode(args[0]), args[1]]);
	}
}

function useMutationInteropRelay<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	...[mutation, options]: UseMutationInteropSupportedArgs<TData, TVariables>
): UseMutationInteropSupportedResult<TData, TVariables> {
	// TODO: shim accepted but unsupported parameters
	const unsupportedParams = ['awaitRefetchQueries', 'optimisticResponse'];
	const unsupportedParam = unsupportedParams.find((param) => options && param in options);
	if (unsupportedParam) {
		throw new Error(`[Relay migration] ${unsupportedParam} not supported yet`);
	}

	const environment = useRelayEnvironment();
	// Extract variables and memoize them to prevent unnecessary re-renders
	const variables = useMemoObj<TVariables>(options?.variables);

	// State to manage the mutation result
	const [data, setData] = useState<TData | undefined>(undefined);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<any>(undefined);

	const onCompletedRef = useRef(options?.onCompleted);
	onCompletedRef.current = options?.onCompleted;

	// Create the mutation function
	const mutate = useCallback(
		(mutationOptions?: MutationFunctionInteropSupportedOptions<TData, TVariables>) => {
			const { optimisticResponse } = mutationOptions || {};
			setLoading(true);
			setError(undefined);

			// TODO: shim accepted but unsupported parameters
			if (optimisticResponse) {
				throw new Error('[Relay migration] optimisticResponse not supported yet');
			}

			const operationVariables = mutationOptions?.variables || variables || {};

			return new Promise<{ data?: TData }>((resolve, reject) => {
				commitMutation(environment, {
					mutation,
					variables: operationVariables,
					onCompleted: (response: any, errors) => {
						if (errors && errors.length > 0) {
							const error = new Error(errors[0].message);
							setError(error);
							reject(error);
						} else {
							setData(response as TData);
							// Call the user-provided onCompleted callback only on success (to match Apollo behavior)
							onCompletedRef.current?.(response);
							resolve({ data: response as TData });
						}
						setLoading(false);
					},
					onError: (error: any) => {
						setLoading(false);
						setError(error);
						reject(error);
					},
				});
			});
		},
		[environment, mutation, variables],
	);

	return [
		mutate,
		{
			data,
			loading,
			error,
		},
	];
}
